import { createContext, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { profileService } from '@/services';
import { accessControlService } from '@/services';

interface AccessControlContextType {
  isLoading: boolean;
  canAccessModule: (moduleCode: string) => boolean;
  hasFeature: (featureCode: string) => boolean;
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  canPerformAction: (moduleCode: string, permissionCode: string | string[], featureCode?: string) => boolean;
  invalidateCache: () => void;
}

const AccessControlContext = createContext<AccessControlContextType | undefined>(undefined);

export function AccessControlProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  const { data: accessProfile, isLoading } = useQuery({
    queryKey: ['access-control-cache', user?.id, companyId],
    queryFn: async () => {
      if (!user?.id || !companyId) return null;
      const result = await accessControlService.resolveSessionAccess(user.id, companyId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id && !!companyId,
  });

  // Cross-tab/cross-user invalidation: poll a tiny version endpoint every
  // 30s. profiles.effective_access_version is bumped by DB triggers on the
  // eight access tables, so a value change here means another session
  // (admin lens, this user in another tab, or a server-side change) altered
  // the effective set. The useEffect below detects mismatch against the
  // version stamped on the last /session response and invalidates the cache.
  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['access-control-cache', user?.id, companyId] });
  }, [queryClient, user?.id, companyId]);

  const versionRef = useRef<number | null>(null);
  useEffect(() => {
    if (accessProfile?.version != null) {
      versionRef.current = accessProfile.version;
    }
  }, [accessProfile?.version]);

  const { data: polledVersion } = useQuery({
    queryKey: ['access-control-version', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await accessControlService.getVersion(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id && !!companyId,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  useEffect(() => {
    const seen = versionRef.current;
    const polled = polledVersion?.version;
    if (seen != null && polled != null && polled !== seen) {
      invalidateCache();
    }
  }, [polledVersion?.version, invalidateCache]);

  const moduleSet = useMemo(
    () => new Set(accessProfile?.moduleCodes ?? []),
    [accessProfile]
  );
  const featureSet = useMemo(
    () => new Set(accessProfile?.featureCodes ?? []),
    [accessProfile]
  );
  const featureModuleMap = useMemo(
    () => accessProfile?.featureModuleMap ?? {},
    [accessProfile]
  );
  const permissionSet = useMemo(
    () => new Set(accessProfile?.permissionCodes ?? []),
    [accessProfile]
  );

  const canAccessModule = useCallback(
    (moduleCode: string) => moduleSet.has(moduleCode),
    [moduleSet]
  );

  const hasFeature = useCallback(
    (featureCode: string) => {
      if (!featureSet.has(featureCode)) return false;
      const parentModule = featureModuleMap[featureCode];
      return parentModule ? moduleSet.has(parentModule) : false;
    },
    [featureSet, featureModuleMap, moduleSet]
  );

  const hasPermission = useCallback(
    (permissionCode: string) => permissionSet.has(permissionCode),
    [permissionSet]
  );

  const hasAnyPermission = useCallback(
    (permissionCodes: string[]) => permissionCodes.some(code => permissionSet.has(code)),
    [permissionSet]
  );

  const canPerformAction = useCallback(
    (moduleCode: string, permissionCode: string | string[], featureCode?: string) => {
      if (!moduleSet.has(moduleCode)) return false;
      if (featureCode && !hasFeature(featureCode)) return false;
      return Array.isArray(permissionCode)
        ? permissionCode.some(code => permissionSet.has(code))
        : permissionSet.has(permissionCode);
    },
    [moduleSet, hasFeature, permissionSet]
  );

  return (
    <AccessControlContext.Provider
      value={{ isLoading, canAccessModule, hasFeature, hasPermission, hasAnyPermission, canPerformAction, invalidateCache }}
    >
      {children}
    </AccessControlContext.Provider>
  );
}

export function useAccessControl() {
  const context = useContext(AccessControlContext);
  if (context === undefined) {
    throw new Error('useAccessControl must be used within an AccessControlProvider');
  }
  return context;
}
